import { useBoolean } from 'ahooks';
import { useState } from 'react';
import { useModifiersBuilder } from '../../../CreateTransactionRuleModal';
import {
  CustomModifierField,
  DynamicResponseModifiersList,
  RuleDescriptionField,
  RuleNameTitleField,
} from '../../../TransactionRuleForm';
import {
  CancelButton,
  FormLayout,
  Section,
} from '../../../TransactionRuleModal';
import { useElementSize } from '../../../hooks';
import { useEditTransactionRuleContext } from '../../EditTransactionRuleContext';
import { useFormDirtyFields } from '../../useFormDirtyFields';
import { EditStaticResponse } from '../EditTransactionModifiersStep';
import { ChangesCounter } from './ChangesCounter';
import { SaveButton } from './SaveButton';

export const UserForm = () => {
  const { submitEdit, cancelEdit, transactionRule, form } =
    useEditTransactionRuleContext();
  const [isLegalResponse, setIsLegalResponse] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useBoolean();
  const { handleSubmit, formState, watch } = form;
  const { isFormDirty } = useFormDirtyFields();

  const responseType = watch('responseType');
  const description = watch('description');
  const dynamicResponseModifiers = watch('dynamicResponseModifiers');

  const [containerRef, { height }] = useElementSize();
  const { buildModifiers, isModifiersFormFieldsDirty } = useModifiersBuilder({
    getFormValues: () => form.getValues(),
  });

  const submitForm = async (refresh = true) => {
    const { name, description } = form.getValues();
    setIsSubmitting.setTrue();
    await submitEdit(
      {
        ...transactionRule,
        name: name.trim(),
        description,
        rules: [
          {
            ...transactionRule.rules[0],
            modifiers: isModifiersFormFieldsDirty(formState.dirtyFields)
              ? buildModifiers()
              : transactionRule.rules[0].modifiers,
          },
        ],
      },
      refresh
    );
    setIsSubmitting.setFalse();
  };

  const isSubmitButtonDisabled = () => {
    const { isValid } = formState;
    return (
      !isLegalResponse ||
      !isValid ||
      !isFormDirty() ||
      isSubmitting ||
      (responseType === 'dynamic' && !dynamicResponseModifiers.length)
    );
  };

  return (
    <FormLayout onSubmit={handleSubmit(submitForm)}>
      <div className="flex flex-col px-5 overflow-hidden h-full">
        <div className="pt-5 flex flex-col gap-2">
          <RuleNameTitleField />
          {!!description?.length && (
            <RuleDescriptionField
              rows={2}
              readOnly
            />
          )}
        </div>
        <Section
          title="Data"
          iconType="modifiers"
          overflowHidden={responseType === 'dynamic'}
        >
          {responseType !== 'dynamic' ? (
            responseType === 'static' ? (
              <div
                className="flex flex-col flex-1 min-h-0"
                ref={containerRef}
              >
                <EditStaticResponse
                  height={height}
                  onError={(errors) => setIsLegalResponse(!errors.length)}
                />
              </div>
            ) : (
              <CustomModifierField />
            )
          ) : (
            <DynamicResponseModifiersList />
          )}
        </Section>
      </div>
      <FormLayout.Footer>
        <ChangesCounter />
        <div className="flex-1" />
        <CancelButton
          onClick={cancelEdit}
          disabled={isSubmitting}
        />
        <SaveButton
          onClick={(shouldRefresh) =>
            handleSubmit(() => submitForm(shouldRefresh))()
          }
          disabled={isSubmitButtonDisabled()}
        />
      </FormLayout.Footer>
    </FormLayout>
  );
};
